import type { LngLat } from './protocol';

/** A perfect city pin is worth the same ceiling as a GeoGuessr world round. */
export const CITY_MAX_POINTS = 5_000;

/** Pins inside this radius are treated as exact; map centroids are not doorways. */
export const CITY_PERFECT_RADIUS_KM = 10;

/** Controls the global-map distance falloff after the perfect radius. */
export const CITY_DISTANCE_DECAY_KM = 2_000;

/**
 * HOW CLOSE COUNTS AS HAVING FOUND A CITY.
 *
 * Coverage asks a yes-or-no question -- "do I know where this is" -- and a
 * city pin does not answer one. A country is hit or missed; a city is scored
 * on a curve that pays something for almost anything, which is right for a
 * SCORE and useless as a claim of knowledge.
 *
 * Without a line drawn somewhere, city coverage counted every city the player
 * had been SHOWN. Pinning Paris in the Pacific added it to the map as found,
 * so "14 found" was really "14 seen" and the number could never go down or
 * mean anything.
 *
 * 250km is about the width of a small country: close enough that nobody lands
 * there without genuinely knowing where the city is, and loose enough to
 * survive a coarse tap on a zoomed-out globe, where a single pixel can be tens
 * of kilometres. It pays about 89% of full marks, so it is well inside the
 * range a player would call a good guess.
 *
 * ONE NUMBER, TUNED IN ONE PLACE. Nothing else should decide this.
 */
export const CITY_FOUND_RADIUS_KM = 250;

const EARTH_RADIUS_KM = 6_371.0088;

function radians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/** Great-circle distance between two MapLibre-order coordinates. */
export function cityDistanceKm(a: LngLat, b: LngLat): number {
  const lat1 = radians(a[1]);
  const lat2 = radians(b[1]);
  const dLat = lat2 - lat1;
  const dLng = radians(b[0] - a[0]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Global city-pin score: 5,000 inside 10 km, then a smooth exponential falloff.
 * There is always a small reward for a geographically sensible guess, while a
 * different continent approaches zero.
 */
export function cityPointsForDistance(distanceKm: number): number {
  if (!Number.isFinite(distanceKm) || distanceKm < 0) {
    throw new RangeError(`cityPointsForDistance: invalid distance ${distanceKm}`);
  }
  if (distanceKm <= CITY_PERFECT_RADIUS_KM) return CITY_MAX_POINTS;
  const scoredKm = distanceKm - CITY_PERFECT_RADIUS_KM;
  return Math.max(
    0,
    Math.min(CITY_MAX_POINTS, Math.round(CITY_MAX_POINTS * Math.exp(-scoredKm / CITY_DISTANCE_DECAY_KM))),
  );
}

export function scoreCityPin(pin: LngLat, target: LngLat): {
  readonly distanceKm: number;
  readonly points: number;
} {
  const distanceKm = cityDistanceKm(pin, target);
  return { distanceKm, points: cityPointsForDistance(distanceKm) };
}
